import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

interface FeatureFlag {
  id: string;
  name: string;
  description: string | null;
  enabledForAll: boolean;
  whitelabelIds: string[];
  uids: string[];
  companyIds: string[];
  createdBy: string | null;
  created_date: string;
  updated_date: string;
}

interface UserOption {
  id: string;
  email: string | null;
  displayName: string | null;
}

interface CompanyOption {
  id: string;
  name: string | null;
}

interface EntityMaps {
  users: Record<string, { email: string | null; displayName: string | null }>;
  companies: Record<string, { name: string | null }>;
}

interface FeatureFlagManagementProps {
  getToken: () => Promise<string>;
}

const FeatureFlagManagement: React.FC<FeatureFlagManagementProps> = ({
  getToken,
}) => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    enabledForAll: false,
    whitelabelIds: [] as string[],
    uids: [] as string[],
    companyIds: [] as string[],
  });

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFlag, setDeletingFlag] = useState<FeatureFlag | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Entity name resolution for display
  const [entityMaps, setEntityMaps] = useState<EntityMaps>({
    users: {},
    companies: {},
  });

  // User/Company search for autocomplete
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);

  // Selected entities for the form (with full info)
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyOption[]>(
    []
  );

  const fetchFlags = useCallback(
    async (search?: string) => {
      setLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const params: Record<string, string | number> = {
          page: page + 1,
          pageSize: rowsPerPage,
        };

        if (search) {
          params.search = search;
        }

        const response = await axios.get(`${hostname}/feature-flags`, {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });

        setFlags(response.data.items);
        setTotalCount(response.data.total);
      } catch (err) {
        setError(parseError(err));
      } finally {
        setLoading(false);
      }
    },
    [getToken, page, rowsPerPage]
  );

  // Resolve entity names for all flags when flags change
  const resolveEntityNames = useCallback(
    async (flagsToResolve: FeatureFlag[]) => {
      const allUids = new Set<string>();
      const allCompanyIds = new Set<string>();

      flagsToResolve.forEach((flag) => {
        flag.uids?.forEach((uid) => allUids.add(uid));
        flag.companyIds?.forEach((id) => allCompanyIds.add(id));
      });

      // Skip if nothing to resolve
      if (allUids.size === 0 && allCompanyIds.size === 0) return;

      try {
        const token = await getToken();
        const response = await axios.post(
          `${hostname}/feature-flags/resolve-entities`,
          {
            uids: Array.from(allUids),
            companyIds: Array.from(allCompanyIds),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setEntityMaps({
          users: { ...entityMaps.users, ...response.data.users },
          companies: { ...entityMaps.companies, ...response.data.companies },
        });
      } catch (err) {
        // Silent fail - just show IDs if resolution fails
        console.error("Failed to resolve entity names:", err);
      }
    },
    [getToken, entityMaps]
  );

  useEffect(() => {
    fetchFlags(searchQuery);
  }, [fetchFlags, page, rowsPerPage]);

  // Resolve entity names when flags are loaded
  useEffect(() => {
    if (flags.length > 0) {
      resolveEntityNames(flags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags]);

  // Search users with debounce
  useEffect(() => {
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setUserOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/feature-flags/search-users`,
          {
            params: { q: userSearchQuery },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setUserOptions(response.data.users);
      } catch (err) {
        console.error("Failed to search users:", err);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userSearchQuery, getToken]);

  // Search companies with debounce
  useEffect(() => {
    if (!companySearchQuery || companySearchQuery.length < 2) {
      setCompanyOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setCompanySearchLoading(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/feature-flags/search-companies`,
          {
            params: { q: companySearchQuery },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCompanyOptions(response.data.companies);
      } catch (err) {
        console.error("Failed to search companies:", err);
      } finally {
        setCompanySearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [companySearchQuery, getToken]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchFlags(value);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleOpenCreate = () => {
    setEditingFlag(null);
    setFormData({
      name: "",
      description: "",
      enabledForAll: false,
      whitelabelIds: [],
      uids: [],
      companyIds: [],
    });
    setSelectedUsers([]);
    setSelectedCompanies([]);
    setUserSearchQuery("");
    setCompanySearchQuery("");
    setUserOptions([]);
    setCompanyOptions([]);
    setDialogOpen(true);
  };

  const handleOpenEdit = async (flag: FeatureFlag) => {
    setEditingFlag(flag);
    setFormData({
      name: flag.name,
      description: flag.description || "",
      enabledForAll: flag.enabledForAll,
      whitelabelIds: flag.whitelabelIds || [],
      uids: flag.uids || [],
      companyIds: flag.companyIds || [],
    });

    // Convert existing IDs to UserOption/CompanyOption objects using entityMaps
    const usersWithInfo: UserOption[] = (flag.uids || []).map((uid) => ({
      id: uid,
      email: entityMaps.users[uid]?.email || null,
      displayName: entityMaps.users[uid]?.displayName || null,
    }));
    const companiesWithInfo: CompanyOption[] = (flag.companyIds || []).map(
      (id) => ({
        id: id,
        name: entityMaps.companies[id]?.name || null,
      })
    );

    setSelectedUsers(usersWithInfo);
    setSelectedCompanies(companiesWithInfo);
    setUserSearchQuery("");
    setCompanySearchQuery("");
    setUserOptions([]);
    setCompanyOptions([]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();

      // Build the payload with IDs extracted from selected entities
      const payload = {
        ...formData,
        uids: selectedUsers.map((u) => u.id),
        companyIds: selectedCompanies.map((c) => c.id),
      };

      if (editingFlag) {
        await axios.put(`${hostname}/feature-flags/${editingFlag.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess("Feature flag updated successfully");
      } else {
        await axios.post(`${hostname}/feature-flags`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess("Feature flag created successfully");
      }

      setDialogOpen(false);
      fetchFlags(searchQuery);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (flag: FeatureFlag) => {
    setDeletingFlag(flag);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingFlag) return;

    setDeleting(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.delete(`${hostname}/feature-flags/${deletingFlag.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess("Feature flag deleted successfully");
      setDeleteDialogOpen(false);
      fetchFlags(searchQuery);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getEntityCount = (flag: FeatureFlag) => {
    return (
      (flag.whitelabelIds?.length || 0) +
      (flag.uids?.length || 0) +
      (flag.companyIds?.length || 0)
    );
  };

  // Helper to format user display name
  const formatUserDisplay = (uid: string) => {
    const user = entityMaps.users[uid];
    if (user) {
      if (user.displayName && user.email) {
        return `${user.displayName} (${user.email})`;
      }
      return user.displayName || user.email || uid;
    }
    return uid;
  };

  // Helper to format company display name
  const formatCompanyDisplay = (companyId: string) => {
    const company = entityMaps.companies[companyId];
    if (company?.name) {
      return company.name;
    }
    return companyId;
  };

  // Format user option for autocomplete display
  const getUserOptionLabel = (option: UserOption) => {
    if (option.displayName && option.email) {
      return `${option.displayName} (${option.email})`;
    }
    return option.displayName || option.email || option.id;
  };

  // Format company option for autocomplete display
  const getCompanyOptionLabel = (option: CompanyOption) => {
    return option.name || option.id;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Feature Flags
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Flag
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by name or description..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Feature Flags Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Entities</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flags.map((flag) => (
                <TableRow key={flag.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FlagIcon
                        fontSize="small"
                        color={flag.enabledForAll ? "primary" : "disabled"}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {flag.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {flag.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {flag.enabledForAll ? (
                      <Chip label="Enabled for all" color="primary" size="small" />
                    ) : getEntityCount(flag) > 0 ? (
                      <Chip
                        label="Partial"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Disabled" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {(flag.whitelabelIds?.length || 0) > 0 && (
                        <Tooltip
                          title={`Whitelabels: ${flag.whitelabelIds.join(", ")}`}
                        >
                          <Chip
                            label={`${flag.whitelabelIds.length} WL`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {(flag.uids?.length || 0) > 0 && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight={600}>
                                Users:
                              </Typography>
                              {flag.uids.map((uid) => (
                                <Box key={uid}>
                                  <Typography variant="caption">
                                    {formatUserDisplay(uid)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                        >
                          <Chip
                            label={`${flag.uids.length} Users`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {(flag.companyIds?.length || 0) > 0 && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight={600}>
                                Companies:
                              </Typography>
                              {flag.companyIds.map((id) => (
                                <Box key={id}>
                                  <Typography variant="caption">
                                    {formatCompanyDisplay(id)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                        >
                          <Chip
                            label={`${flag.companyIds.length} Co`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {getEntityCount(flag) === 0 && !flag.enabledForAll && (
                        <Typography variant="caption" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(flag)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDelete(flag)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {flags.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No feature flags found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {editingFlag ? "Edit Feature Flag" : "Create Feature Flag"}
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., new_dashboard_feature"
              helperText="Unique identifier for this feature flag"
              disabled={!!editingFlag}
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              placeholder="Describe what this feature flag controls"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabledForAll}
                  onChange={(e) =>
                    setFormData({ ...formData, enabledForAll: e.target.checked })
                  }
                />
              }
              label="Enable for all users"
            />

            {!formData.enabledForAll && (
              <>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.whitelabelIds}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, whitelabelIds: newValue })
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Whitelabel IDs"
                      placeholder="Type and press Enter to add"
                      helperText="Enter whitelabel IDs that should have access"
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedUsers}
                  loading={userSearchLoading}
                  getOptionLabel={getUserOptionLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(x) => x}
                  onChange={(_, newValue) => setSelectedUsers(newValue)}
                  onInputChange={(_, newInputValue) =>
                    setUserSearchQuery(newInputValue)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={getUserOptionLabel(option)}
                        size="small"
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">
                          {option.displayName || option.email || option.id}
                        </Typography>
                        {option.email && option.displayName && (
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Users"
                      placeholder="Search by name or email..."
                      helperText="Search for users by name or email to add"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userSearchLoading ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={companyOptions}
                  value={selectedCompanies}
                  loading={companySearchLoading}
                  getOptionLabel={getCompanyOptionLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(x) => x}
                  onChange={(_, newValue) => setSelectedCompanies(newValue)}
                  onInputChange={(_, newInputValue) =>
                    setCompanySearchQuery(newInputValue)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={getCompanyOptionLabel(option)}
                        size="small"
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Typography variant="body2">
                        {option.name || option.id}
                      </Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Companies"
                      placeholder="Search by company name..."
                      helperText="Search for companies by name to add"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {companySearchLoading ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
            {editingFlag ? "Save Changes" : "Create"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Delete Feature Flag
            <IconButton onClick={() => setDeleteDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the feature flag{" "}
            <strong>{deletingFlag?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleDelete}
            loading={deleting}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureFlagManagement;
